//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

import { recordCount, recordEvent, updatePage } from '@tracker/tracker';
import {
  TRACKING_ACTION,
  TRACKING_CATEGORY,
  TRACKING_LABEL,
} from '@tracker/trackerModel';

/**
 * The new enums omit the `CATEGORY_`, `ACTION_`, and `LABEL_` prefixes, so to ensure backwards
 * compatibility for the type interface we use some template literal types to represent
 * the prefixing that happens in the constructor below.
 */
type CATEGORY_PREFIX<T extends PropertyKey> = T extends string
  ? `CATEGORY_${T}`
  : never;
type Categories = Record<
  CATEGORY_PREFIX<keyof typeof TRACKING_CATEGORY>,
  TRACKING_CATEGORY
>;

type ACTION_PREFIX<T extends PropertyKey> = T extends string
  ? `ACTION_${T}`
  : never;
type Actions = Record<
  ACTION_PREFIX<keyof typeof TRACKING_ACTION>,
  TRACKING_ACTION
>;

type LABEL_PREFIX<T extends PropertyKey> = T extends string
  ? `LABEL_${T}`
  : never;
type Labels = Record<LABEL_PREFIX<keyof typeof TRACKING_LABEL>, TRACKING_LABEL>;

interface GaApiLegacy
  extends Categories,
    Actions,
    Labels,
    Record<string, unknown> {}

class GaApiLegacy {
  constructor() {
    /**
     * Copies the new tracking enums over to the legacy GaApi class to ensure backwards
     * compatibility with any instance of the old class.
     */
    Object.entries(TRACKING_CATEGORY).reduce<GaApiLegacy>(
      (acc, [key, value]) => {
        const keyWithPrefix = `CATEGORY_${key}`;
        acc[keyWithPrefix] = value;
        return acc;
      },
      this,
    );
    Object.entries(TRACKING_ACTION).reduce<GaApiLegacy>((acc, [key, value]) => {
      const keyWithPrefix = `ACTION_${key}`;
      acc[keyWithPrefix] = value;
      return acc;
    }, this);
    Object.entries(TRACKING_LABEL).reduce<GaApiLegacy>((acc, [key, value]) => {
      const keyWithPrefix = `LABEL_${key}`;
      acc[keyWithPrefix] = value;
      return acc;
    }, this);
  }

  /**
   * @deprecated Import and use the `recordEvent` method from tracker.ts directly.
   */
  public recordEvent = recordEvent;

  /**
   * @deprecated Import and use the `recordCount` method from tracker.ts directly.
   */
  public recordCount = recordCount;

  /**
   * @deprecated Import and use the `updatePage` method from tracker.ts directly.
   */
  public updatePage = updatePage;
}

/**
 * @deprecated This is the type of the deprecated ga-api class, exported for backwards compatibility.
 * Use methods and tracking constants from `tracker.ts` and `trackerModel.ts` directly.
 */
export type GaApi = GaApiLegacy;

/**
 * @deprecated Use individual methods from tracker.ts, and category/action enums from trackerModel.ts.
 */
const gaApi = new GaApiLegacy();

/**
 * @deprecated Use individual methods from tracker.ts, and category/action enums from trackerModel.ts.
 */
export default gaApi;
